import GiftBox from '@activities/prepaidCard/MyPrepaidCardActivity/components/GiftBox';
import MultipleCard from '@activities/prepaidCard/MyPrepaidCardActivity/components/MultipleCard';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import CtaLayout from '@components/CtaLayout';
import Divider from '@components/Divider';
import Header from '@components/Header';
import PrepaidCardNotes from '@components/prepaid/PrepaidCardNotes';
import TabItem from '@components/tab/TabItem';
import TabList from '@components/tab/TabList';
import colors from '@constants/colors';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import RefreshIcon from '@icons/Refresh';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useStepFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Blank from './components/Blank';
import CardDetail from './components/CardDetail';
import CardSkeleton from './components/CardSkeleton';
import useFavoritePrepaidCards from './hooks/useFavoritePrepaidCards';

interface Params {
    tab?: 'MY' | 'BOX';
}

declare module '@stackflow/config' {
    interface Register {
        MyPrepaidCardActivity: Params;
    }
}

const MyPrepaidCardActivity: ActivityComponentType<'MyPrepaidCardActivity'> = ({ params }) => {
    const { tab = 'MY' } = params;
    const { t } = useTranslation('prepaidCard');
    const { replaceStep } = useStepFlow('MyPrepaidCardActivity');
    const { prepaidCards, isBlank, isLoading } = useFavoritePrepaidCards();
    const queryClient = useQueryClient();
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            const queryKey = tab === 'MY'
                ? userPrepaidCardQueries.list().queryKey
                : userPrepaidCardQueries.giftBoxes._def;
            await queryClient.refetchQueries({ queryKey });
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <AppScreen>
            <CtaLayout header={(
                <Fragment>
                    <Header title={t('title')}
                        trailing={(
                            <RefreshButton type='button'
                                onClick={handleRefresh}
                                disabled={refreshing}
                                css={pressActionEffect}
                                aria-label='refresh'>
                                <RefreshIcon size={24}
                                    color={colors.gray9}
                                    css={refreshing && spinningCss} />
                            </RefreshButton>
                        )} />
                    <TabList variant='line'>
                        <TabItem selected={tab === 'MY'}
                            onClick={() => replaceStep({ tab: 'MY' })}>
                            {t('tabs.my')}
                        </TabItem>
                        <TabItem selected={tab === 'BOX'}
                            onClick={() => replaceStep({ tab: 'BOX' })}>
                            {t('tabs.box')}
                        </TabItem>
                    </TabList>
                </Fragment>
            )}>
                {tab === 'MY' && (
                    <Fragment>
                        {isLoading && <CardSkeleton />}
                        {isBlank && (
                            <>
                                <Blank />
                                <Divider />
                            </>
                        )}
                        {!isBlank && prepaidCards && (
                            <Fragment>
                                <MultipleCard cards={prepaidCards} />
                                <CardDetail />
                                <Divider />
                            </Fragment>
                        )}
                        <PrepaidCardNotes />
                    </Fragment>
                )}
                {tab === 'BOX' && (
                    <GiftBox />
                )}
            </CtaLayout>
        </AppScreen>
    );
};

const RefreshButton = styled.button`
    align-items: center;
    background: transparent;
    display: flex;
    justify-content: center;
    padding: 0;
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const spinningCss = css`
    animation: ${spin} 400ms linear infinite;
`;

export default MyPrepaidCardActivity;
